import { useState, useMemo, useCallback } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Flex,
  Text,
  IconButton,
  Grid,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';

const DAY_LABELS = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];
const MONTHS_SHORT = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
];

function parseDate(str) {
  if (!str) return null;
  const [y, m, d] = str.split('-').map(Number);
  return { year: y, month: m - 1, day: d };
}

function formatDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getCalendarDays(year, month) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const prevMonthDays = new Date(year, month, 0).getDate();

  const days = [];

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push({ day: prevMonthDays - i, current: false, prev: true });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    days.push({ day: d, current: true });
  }

  const totalCells = days.length > 35 ? 42 : 35;
  for (let d = 1; days.length < totalCells; d++) {
    days.push({ day: d, current: false, next: true });
  }

  return days;
}

export default function CalendarPicker({ value, onChange, children, placement = "bottom-start" }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const selected = useMemo(() => parseDate(value), [value]);

  const [viewYear, setViewYear] = useState(() => selected?.year ?? new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => selected?.month ?? new Date().getMonth());
  const [view, setView] = useState('days');
  const [yearsStart, setYearsStart] = useState(() => {
    const y = selected?.year ?? new Date().getFullYear();
    return y - (y % 12);
  });

  const handleOpen = useCallback(() => {
    if (selected) {
      setViewYear(selected.year);
      setViewMonth(selected.month);
      setYearsStart(selected.year - (selected.year % 12));
    }
    setView('days');
    onOpen();
  }, [selected, onOpen]);

  const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  };

  const selectDay = (d) => {
    let y = viewYear, m = viewMonth;
    if (d.prev) { m--; if (m < 0) { m = 11; y--; } }
    if (d.next) { m++; if (m > 11) { m = 0; y++; } }
    onChange(formatDateStr(y, m, d.day));
    onClose();
  };

  const isSelected = (d) =>
    d.current && selected &&
    selected.year === viewYear &&
    selected.month === viewMonth &&
    selected.day === d.day;

  return (
    <Popover isOpen={isOpen} onOpen={handleOpen} onClose={onClose} placement={placement} isLazy>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        w="280px"
        borderRadius="xl"
        border="1px solid"
        borderColor="redesign.border"
        _focus={{ outline: "none" }}
      >
        <PopoverBody p={3}>
          {view === 'days' && (
            <>
              <Flex align="center" justify="space-between" mb={2}>
                <IconButton icon={<ChevronLeftIcon />} size="xs" variant="ghost" onClick={prevMonth} aria-label="Mes anterior" />
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  cursor="pointer"
                  _hover={{ color: "redesign.accent.blue" }}
                  onClick={() => setView('months')}
                >
                  {MONTHS[viewMonth]} {viewYear}
                </Text>
                <IconButton icon={<ChevronRightIcon />} size="xs" variant="ghost" onClick={nextMonth} aria-label="Mes siguiente" />
              </Flex>

              <Grid templateColumns="repeat(7, 1fr)" mb={1}>
                {DAY_LABELS.map(label => (
                  <Text key={label} fontSize="xs" color="redesign.text.muted" textAlign="center" fontWeight="500" py={1}>
                    {label}
                  </Text>
                ))}
              </Grid>

              <Grid templateColumns="repeat(7, 1fr)">
                {days.map((d, i) => (
                  <Flex
                    key={i}
                    justify="center"
                    align="center"
                    h="32px"
                    w="32px"
                    mx="auto"
                    borderRadius="md"
                    cursor="pointer"
                    fontSize="sm"
                    fontWeight={isSelected(d) ? "600" : "400"}
                    color={isSelected(d) ? "white" : d.current ? "redesign.text" : "redesign.text.faint"}
                    bg={isSelected(d) ? "redesign.accent.fill" : "transparent"}
                    _hover={{ bg: isSelected(d) ? "redesign.accent.fill" : "redesign.bg.card" }}
                    onClick={() => selectDay(d)}
                  >
                    {d.day}
                  </Flex>
                ))}
              </Grid>
            </>
          )}

          {view === 'months' && (
            <>
              <Flex align="center" justify="space-between" mb={3}>
                <IconButton icon={<ChevronLeftIcon />} size="xs" variant="ghost" onClick={() => setViewYear(y => y - 1)} aria-label="Año anterior" />
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  cursor="pointer"
                  _hover={{ color: "redesign.accent.blue" }}
                  onClick={() => { setYearsStart(viewYear - (viewYear % 12)); setView('years'); }}
                >
                  {viewYear}
                </Text>
                <IconButton icon={<ChevronRightIcon />} size="xs" variant="ghost" onClick={() => setViewYear(y => y + 1)} aria-label="Año siguiente" />
              </Flex>

              <Grid templateColumns="repeat(3, 1fr)" gap={1}>
                {MONTHS_SHORT.map((m, i) => {
                  const isSel = selected && i === selected.month && viewYear === selected.year;
                  return (
                    <Flex
                      key={m}
                      justify="center"
                      align="center"
                      h="36px"
                      borderRadius="md"
                      cursor="pointer"
                      fontSize="sm"
                      fontWeight={isSel ? "600" : "400"}
                      color={isSel ? "white" : "redesign.text"}
                      bg={isSel ? "redesign.accent.fill" : "transparent"}
                      _hover={{ bg: isSel ? "redesign.accent.fill" : "redesign.bg.card" }}
                      onClick={() => { setViewMonth(i); setView('days'); }}
                    >
                      {m}
                    </Flex>
                  );
                })}
              </Grid>
            </>
          )}

          {view === 'years' && (
            <>
              <Flex align="center" justify="space-between" mb={3}>
                <IconButton icon={<ChevronLeftIcon />} size="xs" variant="ghost" onClick={() => setYearsStart(s => s - 12)} aria-label="Años anteriores" />
                <Text fontSize="sm" fontWeight="600">
                  {yearsStart} – {yearsStart + 11}
                </Text>
                <IconButton icon={<ChevronRightIcon />} size="xs" variant="ghost" onClick={() => setYearsStart(s => s + 12)} aria-label="Años siguientes" />
              </Flex>

              <Grid templateColumns="repeat(3, 1fr)" gap={1}>
                {Array.from({ length: 12 }, (_, i) => yearsStart + i).map(y => {
                  const isSel = selected && y === selected.year;
                  return (
                    <Flex
                      key={y}
                      justify="center"
                      align="center"
                      h="36px"
                      borderRadius="md"
                      cursor="pointer"
                      fontSize="sm"
                      fontWeight={isSel ? "600" : "400"}
                      color={isSel ? "white" : "redesign.text"}
                      bg={isSel ? "redesign.accent.fill" : "transparent"}
                      _hover={{ bg: isSel ? "redesign.accent.fill" : "redesign.bg.card" }}
                      onClick={() => { setViewYear(y); setView('months'); }}
                    >
                      {y}
                    </Flex>
                  );
                })}
              </Grid>
            </>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
